/**
 * Score-entry state of this WebView, for the in-app browser banner and the automatic KakaoTalk
 * exit. Leaving the messenger while caddy ops are still queued strands them in the messenger's
 * IndexedDB: a backgrounded WebView pauses its timers, the caddy logs in again in the browser and
 * keeps scoring there, and when the messenger is reopened later the old ops are sent — score.set is
 * last-write-wins (CLAUDE.md §9 rule 4, §15 #157), so they would overwrite the newer values with no
 * conflict shown. So: no automatic exit while a scorer session or queued ops exist here, and the
 * banner's button waits until the queue is empty.
 */
import { getSession } from '../../lib/auth-store';
import { lastCaddyLink } from '../../features/caddy/lib/session';
import { selfEntryOwner } from '../../features/clubs/lib/self-entry-session';

/** A CADDY session, or the club SELF_ENTRY session in the PLAYER slot. */
export function holdsScorerSession(): boolean {
  if (getSession('CADDY')) return true;
  const owner = selfEntryOwner();
  const player = getSession('PLAYER');
  return !!owner && !!player && owner.token === player.token;
}

/** This WebView has been used for score entry (so its caddy queue may hold ops). */
export function usedForScoring(): boolean {
  return holdsScorerSession() || lastCaddyLink() !== null;
}

/**
 * Unsent ops in the caddy queue (all scorers; 0 when IndexedDB is unavailable). Loaded lazily so
 * the IndexedDB code stays in the caddy chunk.
 */
export async function countPendingOps(): Promise<number> {
  const { countQueuedOps } = await import('../../features/caddy/lib/db');
  return countQueuedOps();
}
